import { useEffect, useRef } from 'react'
import { useMsal } from '@azure/msal-react'
import { refreshAuthToken } from '../api-utils/token-refresh-interceptor'

export const useTokenRefresh = () => {
  const { instance } = useMsal()
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null)
  const isRefreshingRef = useRef<boolean>(false)
  const lastVisibilityCheckRef = useRef<number>(0)

  useEffect(() => {
    const REFRESH_INTERVAL = 50 * 60 * 1000 // 50 minutes

    const scheduleTokenRefresh = () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current)
      }

      refreshTimerRef.current = setInterval(async () => {
        if (isRefreshingRef.current) {
          console.log('Refresh already in progress, skipping...')
          return
        }

        isRefreshingRef.current = true
        try {
          const success = await refreshAuthToken(instance)
          if (!success) {
            console.error('Scheduled token refresh failed')
          }
        } finally {
          isRefreshingRef.current = false
        }
      }, REFRESH_INTERVAL)
    }

    scheduleTokenRefresh()

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now()

        // Debounce: Don't check more than once every 10 seconds
        if (now - lastVisibilityCheckRef.current < 10000) {
          return
        }
        lastVisibilityCheckRef.current = now

        // Don't refresh if already refreshing
        if (isRefreshingRef.current) {
          return
        }

        const lastRefresh = sessionStorage.getItem('lastTokenRefresh')

        if (lastRefresh) {
          const timeSinceRefresh = now - parseInt(lastRefresh, 10)
          // If more than 45 minutes since last refresh, refresh the token
          if (timeSinceRefresh > 45 * 60 * 1000) {
            isRefreshingRef.current = true
            try {
              await refreshAuthToken(instance)
            } finally {
              isRefreshingRef.current = false
            }
          }
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current)
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [instance])
}
